// LinkPropertyDialog — "Link the AppFolio property" on the Owners page
// (docs/city-utility-agreement-plan.md §6.2).
//
// The signed City utilities agreement is attached to the PROPERTY in AppFolio,
// and a property WHP is just taking on usually is not in AppFolio when the owner
// signs. The server links it by itself once a property with exactly the same
// address appears; when the address differs (a unit suffix, "Street" vs "St"),
// staff pick the property here. The pick is checked against the onboarding's
// address server-side — a mismatch comes back with both addresses, and staff can
// link anyway.
//
// Names and addresses only: the property id is the result row's key and the
// request's `propertyId`, never text (docs/no-ids-in-ui.md).

import { useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { RecipientResultList } from '@/components/ownerSync/RecipientResultList';
import {
  LINK_PROPERTY_LABEL,
  propertyPickerMatches,
  propertyResultRow,
  streetLineOf,
  syncActionErrorMessage,
  type LinkablePropertyOption,
  type LinkPropertyMismatch,
  type OwnerSyncOpView,
} from '@/lib/ownerSync';
import { linkOwnerIntakeProperty } from '@/lib/ownerSyncClient';
import { ApiError } from '@/lib/api';

export function LinkPropertyDialog({
  caseId,
  op,
  intakeAddress,
  loadProperties,
  onClose,
  onLinked,
}: {
  /** The onboarding (EPMA) the step belongs to — addresses the request only. */
  caseId: string;
  /** The waiting step; null = closed. */
  op: OwnerSyncOpView | null;
  /** The onboarding's property address (seeds the search with its street line). */
  intakeAddress: string | null;
  /** Every AppFolio property (GET /api/properties). */
  loadProperties: () => Promise<LinkablePropertyOption[]>;
  onClose: () => void;
  /** After a successful link — the page reloads the steps. */
  onLinked: (picked: LinkablePropertyOption, released: number) => void;
}): React.JSX.Element | null {
  const open = op !== null;
  const [properties, setProperties] = useState<LinkablePropertyOption[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState(streetLineOf(intakeAddress));
  const [selected, setSelected] = useState<LinkablePropertyOption | null>(null);
  const [mismatch, setMismatch] = useState<LinkPropertyMismatch | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on every open; load the property list once per open.
  useEffect(() => {
    if (!open) return;
    setQuery(streetLineOf(intakeAddress));
    setSelected(null);
    setMismatch(null);
    setError(null);
    setLoadError(null);
    let cancelled = false;
    if (properties === null) {
      void (async () => {
        try {
          const list = await loadProperties();
          if (!cancelled) setProperties(list);
        } catch {
          if (!cancelled) setLoadError('Could not load the AppFolio properties just now. Try again in a moment.');
        }
      })();
    }
    return () => {
      cancelled = true;
    };
    // Re-run per opened step only (the list, once loaded, is kept for the page visit).
  }, [open, op?.opKey]);

  const matches = useMemo(() => propertyPickerMatches(properties ?? [], query), [properties, query]);

  const confirm = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setError(null);
    try {
      const out = await linkOwnerIntakeProperty(caseId, selected.propertyId, mismatch !== null);
      if (out.kind === 'mismatch') {
        setMismatch(out.mismatch);
        return;
      }
      onLinked(selected, out.released);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? syncActionErrorMessage(err.body, "Couldn't link that property.")
          : "Couldn't link that property.",
      );
    } finally {
      setBusy(false);
    }
  };

  const pick = (key: string) => {
    const p = matches.find((m) => m.propertyId === key) ?? null;
    setSelected(p);
    setMismatch(null);
    setError(null);
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={LINK_PROPERTY_LABEL}
      description={
        intakeAddress
          ? `Which AppFolio property is ${intakeAddress}? The signed City utilities agreement is attached to it in AppFolio.`
          : 'Which AppFolio property is this? The signed City utilities agreement is attached to it in AppFolio.'
      }
      confirmLabel={mismatch ? 'Link anyway' : 'Link property'}
      tone="primary"
      busy={busy}
      busyLabel="Linking…"
      error={error}
      confirmDisabled={!selected}
      onConfirm={() => void confirm()}
    >
      <div className="stack-sm" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="field" style={{ margin: 0 }}>
          <label htmlFor={`link-property-${caseId}`}>The property in AppFolio</label>
          <input
            id={`link-property-${caseId}`}
            type="search"
            className="input"
            placeholder="Street address or property name"
            value={query}
            disabled={busy}
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="helper">
            {properties === null && !loadError ? 'Loading the AppFolio properties…' : 'Type part of the address, then choose the property.'}
          </span>
        </div>
        {loadError && <p className="oc-meta" style={{ margin: 0 }}>{loadError}</p>}
        {properties !== null && query.trim() !== '' && (
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            <RecipientResultList
              rows={matches.map(propertyResultRow)}
              selectedKey={selected?.propertyId ?? null}
              onPick={pick}
              emptyText="No AppFolio property matches. If it is not set up in AppFolio yet, close this — the form is attached automatically once it is."
            />
          </div>
        )}
        {mismatch && (
          <div className="oc-panel" role="status" data-testid="link-property-mismatch">
            <p style={{ margin: 0 }}>{mismatch.message}</p>
            <p className="oc-meta" style={{ margin: 'var(--space-2) 0 0' }}>
              This onboarding: {mismatch.intakeAddress || '—'}
              <br />
              In AppFolio: {mismatch.appfolioAddress || 'not found'}
            </p>
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
}
